<template>
  <section
    v-if="visibleSections.length > 0"
    class="rounded-2xl border bg-surface p-6 shadow-sm"
    :aria-labelledby="titleId"
    data-testid="setup-panel"
  >
    <div class="flex items-start justify-between gap-4">
      <div class="min-w-0">
        <h2 :id="titleId" class="text-lg font-semibold text-title">{{ t('homebase.setup.title') }}</h2>
        <p class="mt-1 text-sm text-secondary">{{ t('homebase.setup.subtitle') }}</p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        class="shrink-0"
        :disabled="hiding"
        data-testid="setup-panel-hide"
        @click="hide"
      >
        <template #icon><LumiIcon name="remove" aria-hidden="true" /></template>
        {{ t('homebase.setup.hide') }}
      </Button>
    </div>

    <div class="mt-5 gap-x-8 gap-y-6 md:columns-2 xl:columns-3">
      <div
        v-for="section in visibleSections"
        :key="section.id"
        class="mb-6 break-inside-avoid"
        :data-testid="`setup-section-${section.id}`"
      >
        <h3 class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-secondary">
          <component :is="section.icon" class="h-4 w-4" aria-hidden="true" />
          {{ t(`homebase.setup.sections.${section.id}`) }}
        </h3>
        <ul class="mt-2 space-y-1">
          <li v-for="entry in section.entries" :key="entry.key">
            <button
              type="button"
              class="flex w-full items-start justify-between gap-3 rounded-xl px-3 py-2 text-left hover:bg-surface-wash focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
              :data-testid="`setup-entry-${entry.key}`"
              @click="open(entry.target)"
            >
              <span class="min-w-0">
                <span class="block text-sm font-semibold text-title">{{ t(`homebase.setup.entries.${entry.key}.title`) }}</span>
                <span class="mt-0.5 block text-sm text-secondary">{{ t(`homebase.setup.entries.${entry.key}.hint`) }}</span>
              </span>
              <LumiIcon name="next" class="mt-0.5 h-4 w-4 shrink-0 text-secondary" aria-hidden="true" />
            </button>
          </li>
        </ul>
      </div>
    </div>

    <div class="border-t pt-4">
      <RouterLink
        :to="SETUP_PAGE_PATH"
        class="inline-flex items-center gap-1 text-sm font-semibold text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
        data-testid="setup-panel-view-all"
      >
        {{ t('homebase.setup.viewAll') }}
        <LumiIcon name="next" class="h-4 w-4" aria-hidden="true" />
      </RouterLink>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Button, LumiIcon } from '@lumi/ui';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useApiMutation, useToast } from '@lumi/app-kit';
import { useTenantsStore } from '@/stores/tenants';
import { useAuthStore } from '@/stores/auth';
import { useOnboardingStore } from '@/stores/onboarding';
import { SETUP_PAGE_PATH, SETUP_SECTIONS, setupSurfaceEligible, type SetupTarget } from '@/utils/setupPanel';
import { useSetupSections } from '@/utils/setupEntriesAccess';
import { userApi } from '@/utils/api';

// The quiet "Lumi einrichten" panel at the top of Suite Home. It asks each BOOKED
// module which setup screens this person may open (GET /setup-entries — the module
// is the only authority; no client-side role flag is OR-ed into its answer), shows
// only those, and stays absent (never an empty shell) when nothing survives. The
// suite section is the one exception: it stands on the membership flags, no call.
// `accessibleModules`: the module keys this person can open from the launcher. A
// module's answer is a hint about ITS screens; an entry for a module the person
// cannot open would be a click that does nothing, so it is dropped here.
const props = defineProps<{ accessibleModules: string[] }>();
const emit = defineEmits<{ (e: 'navigate', moduleKey: string, deepLink: string): void }>();

const { t } = useI18n();
const router = useRouter();
const toast = useToast();
const store = useTenantsStore();
const onboardingStore = useOnboardingStore();
const authStore = useAuthStore();

const titleId = 'setup-panel-title';

const tenant = computed(() => store.activeTenant);

// Every precondition, before a single request goes out. A demo, a persona mask, a
// borrowed (support) workspace, an unfinished first-run or a hidden panel never
// even asks the modules.
const eligible = computed(
  () =>
    setupSurfaceEligible({
      tenant: tenant.value,
      firstRunResolved: onboardingStore.firstRunResolved,
      isDemo: onboardingStore.isDemo,
      isMasked: onboardingStore.isMasked,
    }) && tenant.value?.setupPanelHidden !== true,
);

// The module sections are asked only for modules the workspace really has booked
// (never a demo showroom module). The answer is shared with the full-list page
// and the sidebar entry (utils/setupEntriesAccess.ts): one per tenant and user.
const { sections } = useSetupSections({
  eligible: () => eligible.value,
  userId: () => authStore.user?.userId,
  tenantSlug: () => tenant.value?.slug,
  bookedModules: () => {
    const mods = tenant.value?.modules ?? [];
    return SETUP_SECTIONS.flatMap((s) => (s.source.kind === 'module' ? [s.source.moduleKey] : [])).filter((key) =>
      mods.some((m) => m.moduleKey === key && (m.status === 'active' || m.status === 'trial')),
    );
  },
  accessibleModules: () => props.accessibleModules,
  suiteAdmin: () => store.hasAdminRole(tenant.value),
  version: () => store.dataVersion,
});

// The panel is exactly the short list: a rare key in a module's answer never adds
// a row, and never makes an otherwise empty section or panel appear.
const visibleSections = computed(() =>
  sections.value.filter((s) => s.short.length > 0).map((s) => ({ id: s.id, icon: s.icon, entries: s.short })),
);

function open(target: SetupTarget) {
  if (target.kind === 'route') {
    void router.push(target.path);
    return;
  }
  emit('navigate', target.moduleKey, target.deepLink);
}

// Hide for this person in this workspace for good (server-side, every device).
// CONSUMER (the hide function below): reads `outcome.ok`; on failure the panel stays and
// `outcome.formError` is toasted, like the clock card next to it — a second global
// notice would say the same thing twice.
const { pending: hiding, mutate: putHidden } = useApiMutation<void, [string]>({
  tier: 'inlineEdit',
  errorChannel: 'handled',
  run: async (slug) => {
    await userApi.put('/tenant/setup-panel', { hidden: true }, { headers: { 'X-Tenant-Slug': slug }, suppressGlobalError: true });
  },
});

async function hide() {
  const tn = tenant.value;
  if (!tn || hiding.value) return;
  const outcome = await putHidden(tn.slug);
  if (!outcome.ok) {
    toast.error(outcome.formError);
    return;
  }
  store.setSetupPanelHidden(tn.id, true);
}
</script>
